<template>
  <div class="movie">
    <div class="movie_inner">
      <video
        ref="videoElement"
        :autoplay="true"
        :muted="isMuted"
        :loop="true"
        playsinline
        webkit-playsinline
        x5-playsinline
        height="100%"
        :style="{ display: isAudioMode ? 'none' : 'block' }"
        @canplaythrough="onCanPlayThrough"
        @error="onError"
        @timeupdate="onTimeUpdate"
        @loadedmetadata="onLoadedMetadata"
        @play="onPlay"
        @pause="onPause"
      ></video>
      <!-- 音声モード用のaudio要素 -->
      <audio
        ref="audioElement"
        :autoplay="isAudioMode"
        :muted="isMuted"
        :loop="true"
        :style="{ display: 'none' }"
        @timeupdate="onAudioTimeUpdate"
        @loadedmetadata="onAudioLoadedMetadata"
        @play="onAudioPlay"
        @pause="onAudioPause"
        @error="onAudioError"
      ></audio>
      <!-- シークバーはアーカイブモード（録画再生）の時のみ表示 -->
      <div v-if="duration > 0 && isArchive" class="controls">
        <span class="time-display">{{ formattedCurrentTime }}</span>
        <input
          type="range"
          class="custom-slider"
          min="0"
          :max="duration"
          step="0.01"
          v-model="currentTime"
          @input="onSeek"
        />
        <span class="time-display">{{ formattedDuration }}</span>
      </div>
      <!-- 音声モード時の表示 -->
      <div v-if="isAudioMode" class="audio-mode-indicator">
        <div class="audio-mode-content">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
            <path
              d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
            />
          </svg>
          <p>音声モード</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, nextTick, onMounted, computed, defineEmits } from "vue";
import Hls from "hls.js";

const props = defineProps({
  videourl: {
    type: String,
    default: null,
  },
  videourlforondemand: {
    type: String,
    default: null,
  },
  isMuted: {
    type: Boolean,
    default: true,
  },
  isAudioMode: {
    type: Boolean,
    default: false,
  },
  isArchive: {
    type: Boolean,
    default: false,
  }, // アーカイブモードかどうか（ライブ配信時はfalse）
});

const emit = defineEmits(["timeupdate"]);

const videoElement = ref(null);
const audioElement = ref(null);
const currentTime = ref(0);
const duration = ref(0);
let hls = null;
let audioHls = null;

const isHls = (url) => {
  console.log(`Checking if URL is HLS: ${url}`);
  return url && (url.endsWith(".m3u8") || url.includes(".m3u8?"));
};

const loadVideo = (url) => {
  console.log(`Attempting to load video from URL: ${url}`);
  if (hls) {
    hls.destroy();
    hls = null;
  }

  if (url) {
    if (isHls(url)) {
      console.log("HLS stream detected");
      if (Hls.isSupported()) {
        hls = new Hls();
        hls.attachMedia(videoElement.value);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          console.log("HLS media attached");
          hls.loadSource(url);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log("HLS manifest loaded, attempting to play the video");
            videoElement.value.play().catch((error) => {
              console.error(`Failed to play video: ${error}`);
            });
          });
        });
        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error(`HLS error: ${data.type}`, data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error("ネットワークエラー: ストリームに接続できません");
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error("メディアエラー: 動画データの読み込みに失敗しました");
                break;
              default:
                console.error("致命的なエラーが発生しました");
                break;
            }
          }
        });
      } else {
        console.warn("HLS not supported by this browser");
        videoElement.value.src = url;
      }
    } else {
      console.log("MP4 file detected");
      videoElement.value.src = url;
      videoElement.value.addEventListener("loadedmetadata", () => {
        console.log("Metadata loaded, attempting to play the video");
        videoElement.value.play().catch((error) => {
          console.error(`Failed to play video: ${error}`);
        });
      });
    }
  } else {
    console.warn("No valid video URL provided.");
  }
};

// 音声モード用のaudio要素にストリームを読み込む
const loadAudio = (url, seekTime = null) => {
  console.log(`Attempting to load audio from URL: ${url}, seekTime: ${seekTime}`);
  if (audioHls) {
    audioHls.destroy();
    audioHls = null;
  }

  if (url && audioElement.value) {
    if (isHls(url)) {
      console.log("HLS audio stream detected");
      if (Hls.isSupported()) {
        audioHls = new Hls();
        audioHls.attachMedia(audioElement.value);
        audioHls.on(Hls.Events.MEDIA_ATTACHED, () => {
          console.log("HLS audio media attached");
          audioHls.loadSource(url);
          audioHls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log("HLS audio manifest loaded");
            // シーク可能になるまで待つ
            const seekToTime = seekTime;
            const trySeek = () => {
              if (audioElement.value && seekToTime !== null && seekToTime >= 0) {
                try {
                  audioElement.value.currentTime = seekToTime;
                  console.log(`Audio seeked to: ${seekToTime}`);
                } catch (error) {
                  console.warn("Failed to seek audio, will retry:", error);
                  // シークに失敗した場合は少し待ってから再試行
                  setTimeout(trySeek, 100);
                  return;
                }
              }
              audioElement.value.play().catch((error) => {
                console.error(`Failed to play audio: ${error}`);
              });
            };
            // canplayイベントでシーク可能になるまで待つ
            audioElement.value.addEventListener(
              "canplay",
              () => {
                trySeek();
              },
              { once: true }
            );
            // 既にcanplay状態の場合は即座にシーク
            if (audioElement.value.readyState >= 2) {
              trySeek();
            }
          });
        });
        audioHls.on(Hls.Events.ERROR, (event, data) => {
          console.error(`HLS audio error: ${data.type}`, data);
        });
      } else {
        console.warn("HLS not supported by this browser for audio");
        audioElement.value.src = url;
        if (seekTime !== null && seekTime >= 0) {
          audioElement.value.addEventListener("loadedmetadata", () => {
            audioElement.value.currentTime = seekTime;
            console.log(`Audio seeked to: ${seekTime}`);
          });
        }
      }
    } else {
      console.log("MP4 audio file detected");
      audioElement.value.src = url;
      audioElement.value.addEventListener("loadedmetadata", () => {
        console.log("Audio metadata loaded, attempting to play");
        // 再生位置を設定（動画の現在位置から継続）
        if (seekTime !== null && seekTime >= 0) {
          audioElement.value.currentTime = seekTime;
          console.log(`Audio seeked to: ${seekTime}`);
        }
        audioElement.value.play().catch((error) => {
          console.error(`Failed to play audio: ${error}`);
        });
      });
    }
  }
};

const onCanPlayThrough = () => {
  console.log("Video can play through without interruption");
  if (videoElement.value) {
    videoElement.value.play();
  }
};

const onError = (event) => {
  console.error("Failed to load video:", event);
  if (videoElement.value) {
    console.error("Failed URL:", videoElement.value.src);
    if (videoElement.value.error) {
      const error = videoElement.value.error;
      const errorMessages = {
        1: "MEDIA_ERR_ABORTED - ユーザーが動画の読み込みを中止しました",
        2: "MEDIA_ERR_NETWORK - ネットワークエラーにより動画の読み込みに失敗しました",
        3: "MEDIA_ERR_DECODE - 動画のデコードに失敗しました",
        4: "MEDIA_ERR_SRC_NOT_SUPPORTED - 動画形式がサポートされていません、またはソースが見つかりません",
      };
      console.error("エラー:", errorMessages[error.code] || "不明なエラー");
    }
  }
};

const determineVideoUrl = () => {
  if (props.videourl) {
    return props.videourl;
  } else if (props.videourlforondemand) {
    return props.videourlforondemand;
  } else {
    return null;
  }
};

const updateVideoSource = () => {
  nextTick(() => {
    const url = determineVideoUrl();
    if (url) {
      loadVideo(url);
    } else {
      console.warn("Both videourl and videourlforondemand are null or invalid.");
    }
  });
};

const onTimeUpdate = () => {
  if (videoElement.value && !props.isAudioMode) {
    currentTime.value = videoElement.value.currentTime;
    emit("timeupdate", currentTime); // 親コンポーネントに送信
  }
};

const onAudioTimeUpdate = () => {
  if (audioElement.value && props.isAudioMode) {
    currentTime.value = audioElement.value.currentTime;
    emit("timeupdate", currentTime); // 親コンポーネントに送信
  }
};

const onLoadedMetadata = () => {
  if (videoElement.value && !props.isAudioMode) {
    duration.value = videoElement.value.duration;
    // Media Session APIを設定してバックグラウンド再生を有効化
    setupMediaSession(videoElement.value);
  }
};

const onAudioLoadedMetadata = () => {
  if (audioElement.value && props.isAudioMode) {
    duration.value = audioElement.value.duration;
    // Media Session APIを設定してバックグラウンド再生を有効化
    setupMediaSession(audioElement.value);
  }
};

// Media Session APIを設定してiPhoneでのバックグラウンド再生を有効化
const setupMediaSession = (mediaElement) => {
  if ("mediaSession" in navigator && mediaElement) {
    // メディアメタデータを設定
    navigator.mediaSession.metadata = new MediaMetadata({
      title: "ライブ配信",
      artist: "配信中",
      artwork: [
        // 必要に応じてアートワークを追加
        // { src: '/img/icon.png', sizes: '96x96', type: 'image/png' },
      ],
    });

    // メディアアクションのハンドラーを設定
    navigator.mediaSession.setActionHandler("play", () => {
      mediaElement.play().catch((error) => {
        console.error("Failed to play media:", error);
      });
    });

    navigator.mediaSession.setActionHandler("pause", () => {
      mediaElement.pause();
    });

    // 再生状態を更新
    const updatePlaybackState = () => {
      if (mediaElement.paused) {
        navigator.mediaSession.playbackState = "paused";
      } else {
        navigator.mediaSession.playbackState = "playing";
      }
    };

    // 再生状態の変更を監視
    mediaElement.addEventListener("play", updatePlaybackState);
    mediaElement.addEventListener("pause", updatePlaybackState);
  }
};

const onPlay = () => {
  if ("mediaSession" in navigator && !props.isAudioMode) {
    navigator.mediaSession.playbackState = "playing";
  }
};

const onPause = () => {
  if ("mediaSession" in navigator && !props.isAudioMode) {
    navigator.mediaSession.playbackState = "paused";
  }
};

const onAudioPlay = () => {
  if ("mediaSession" in navigator && props.isAudioMode) {
    navigator.mediaSession.playbackState = "playing";
  }
};

const onAudioPause = () => {
  if ("mediaSession" in navigator && props.isAudioMode) {
    navigator.mediaSession.playbackState = "paused";
  }
};

const onAudioError = (event) => {
  console.error("Failed to load audio:", event);
};

const onSeek = () => {
  if (props.isAudioMode && audioElement.value) {
    audioElement.value.currentTime = currentTime.value;
  } else if (videoElement.value) {
    videoElement.value.currentTime = currentTime.value;
  }
};

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const formattedCurrentTime = computed(() => formatTime(currentTime.value));
const formattedDuration = computed(() => formatTime(duration.value));
watch(
  () => props.videourlforondemand,
  (newUrl) => {
    if (newUrl) {
      console.log("videourlforondemandが更新されました。動画ソースを更新します");
      updateVideoSource();
    }
  }
);

// videourlの変更も監視（重要：非同期で設定されるURLを検知するため）
watch(
  () => props.videourl,
  (newUrl) => {
    if (newUrl) {
      console.log("videourlが更新されました。動画ソースを更新します");
      updateVideoSource();
    }
  }
);

// 音声モードの切り替えを監視
watch(
  () => props.isAudioMode,
  (isAudioMode) => {
    const url = determineVideoUrl();
    if (url) {
      if (isAudioMode) {
        // 音声モードに切り替え：動画の現在位置を取得してaudioを同じ位置から再生
        let seekTime = null;
        if (videoElement.value && !isNaN(videoElement.value.currentTime)) {
          seekTime = videoElement.value.currentTime;
          console.log(`Switching to audio mode at time: ${seekTime}`);
        }
        if (videoElement.value) {
          videoElement.value.pause();
        }
        loadAudio(url, seekTime);
      } else {
        // 通常モードに切り替え：audioの現在位置を取得して動画を同じ位置から再生
        let seekTime = null;
        if (audioElement.value && !isNaN(audioElement.value.currentTime)) {
          seekTime = audioElement.value.currentTime;
          console.log(`Switching to video mode at time: ${seekTime}`);
        }
        if (audioElement.value) {
          audioElement.value.pause();
        }
        if (videoElement.value) {
          if (seekTime !== null && seekTime >= 0) {
            videoElement.value.currentTime = seekTime;
            console.log(`Video seeked to: ${seekTime}`);
          }
          videoElement.value.play().catch((error) => {
            console.error(`Failed to play video: ${error}`);
          });
        }
      }
    }
  }
);

onMounted(() => {
  updateVideoSource();

  // iOSでのバックグラウンド再生を有効化するための追加設定
  nextTick(() => {
    if (videoElement.value) {
      // iOS Safariでのバックグラウンド再生を許可
      videoElement.value.setAttribute("playsinline", "true");
      videoElement.value.setAttribute("webkit-playsinline", "true");
    }
    // 音声モードがONの場合はaudio要素を読み込む（初期状態なのでseekTimeは0）
    if (props.isAudioMode) {
      const url = determineVideoUrl();
      if (url) {
        loadAudio(url, 0);
      }
    }
  });
});
</script>

<style scoped>
.movie {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: black; /* 必要に応じて背景色を設定 */
}
.movie .movie_inner {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}
.movie .movie_inner video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
}
.controls {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.custom-slider {
  flex: 1;
  -webkit-appearance: none; /* Remove default style */
  appearance: none;
  height: 8px;
  background: #ddd;
  border-radius: 4px;
  outline: none;
  opacity: 0.7;
  transition: opacity 0.2s;
  z-index: 5; /* z-indexを追加 */
}
.custom-slider:hover {
  opacity: 1;
}
.custom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: rgb(255, 255, 255);
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
  transform: translateY(-5px); /* つまみの位置を少し上に移動 */
}
.custom-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #4caf50;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
  transform: translateY(-5px); /* つまみの位置を少し上に移動 */
}
.custom-slider::-ms-thumb {
  width: 20px;
  height: 20px;
  background: #4caf50;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
  transform: translateY(-5px); /* つまみの位置を少し上に移動 */
}
.custom-slider::-webkit-slider-runnable-track {
  width: 100%;
  height: 8px;
  background: #ddd;
  border-radius: 4px;
}
.custom-slider::-moz-range-track {
  width: 100%;
  height: 8px;
  background: #ddd;
  border-radius: 4px;
}
.custom-slider::-ms-track {
  width: 100%;
  height: 8px;
  background: transparent;
  border-color: transparent;
  border-width: 8px 0;
  color: transparent;
}
.custom-slider::-ms-fill-lower {
  background: #ddd;
  border-radius: 4px;
}
.custom-slider::-ms-fill-upper {
  background: #ddd;
  border-radius: 4px;
}
.time-display {
  color: white;
  font-size: 14px;
  min-width: 50px;
  text-align: center;
  filter: drop-shadow(2px 2px 5px rgba(0, 0, 0, 0.5)); /* ドロップシャドウを追加 */
}
.audio-mode-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}
.audio-mode-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: white;
  text-align: center;
}
.audio-mode-content p {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  filter: drop-shadow(2px 2px 5px rgba(0, 0, 0, 0.5));
}
@media (min-width: 960px) {
  .movie {
    width: auto;
    height: 80vh;
  }
  .movie .movie_inner {
    width: auto;
    height: 80vh;
    overflow: hidden;
  }
  .vjs_video_3-dimensions {
    width: auto;
    height: 80vh;
  }
  .movie .movie_inner video {
    position: initial;
    width: auto;
    height: 80vh;
    object-fit: cover;
  }
}
/* スマホ用のスタイル */
@media (max-width: 960px) {
  .custom-slider {
    margin-bottom: 20px; /* シークバーの位置を上に上げるためのマージンを追加 */
    transform: translateY(-10px); /* つまみの位置をさらに上に移動 */
  }

  .controls {
    bottom: 30px; /* スマホでのシークバー全体をもう少し上に配置 */
  }
  .time-display {
    transform: translateY(-23px); /* 時間表示も同じように上に移動 */
  }
  .custom-slider {
    height: 13px; /* スマホの場合にシークバーを太く */
  }
}
</style>
